import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { User } from '@/types/asset-audit';

export interface UserAvatarProps {
  name?: string;
  avatarUrl?: string;
  user?: User;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showTooltip?: boolean;
  showName?: boolean;
}

const sizeConfig = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-12 w-12 text-lg',
  xl: 'h-16 w-16 text-xl',
};

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserAvatar({
  name,
  avatarUrl,
  user,
  size = 'md',
  className,
  showTooltip = true,
  showName = false,
}: UserAvatarProps) {
  const displayName = user?.name ?? name ?? 'Unknown';
  const displayAvatarUrl = user?.avatar ?? avatarUrl;
  const initials = getInitials(displayName);

  const avatar = (
    <Avatar
      className={cn(sizeConfig[size], className)}
      title={showTooltip ? displayName : undefined}
    >
      {displayAvatarUrl && <AvatarImage src={displayAvatarUrl} alt={displayName} />}
      <AvatarFallback className="bg-primary/10 text-primary font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  if (showName) {
    return (
      <div className="flex items-center gap-2">
        {avatar}
        <span className="text-sm font-medium">{displayName}</span>
      </div>
    );
  }

  return avatar;
}
